<template>
    <div id="navbar-content" class="navbar__content" :class="{'navbar__content--open': isOpen}" data-testid="navbar-content">
        <NavbarMenu
            v-if="hasMenuItems"
            :nav="nav"
            :open-menu-index="openMenuIndex"
            @toggle-menu="$emit('toggle-menu', $event)"
            @close-menu="$emit('close-menu')"
        />
        <NavbarActions :is-submenu-open="isSubmenuOpen" />
    </div>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import type {NavbarConfig} from '@navbar-unlogged-types';
    import NavbarActions from './NavbarActions.vue';
    import NavbarMenu from './NavbarMenu.vue';

    const props = defineProps<{
        isOpen: boolean;
        nav: NavbarConfig;
        openMenuIndex: number | null;
    }>();

    defineEmits<{
        'toggle-menu': [index: number];
        'close-menu': [];
    }>();

    const hasMenuItems = computed(() => props.nav && props.nav.items && props.nav.items.length > 0);
    const isSubmenuOpen = computed(() => props.openMenuIndex !== null);
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    /* mobile-first */
    .navbar__content {
        display: flex;
        flex-direction: column;
        background-color: var(--joy-color-background-default);
        position: fixed;
        left: 0;
        width: 100vw;
        padding-top: var(--navbar-top-mobile-height);

        /* Navbar `transform` is the containing block. Stretch from below the bar to the viewport bottom. */
        top: 100%;
        bottom: calc(100% - 100vh);
        bottom: calc(100% - 100dvh);
        overflow: hidden;
        z-index: 1;
        opacity: 0;
        visibility: hidden;
        transform: translateY(-20px);
        transition:
            opacity var(--navbar-transition-duration) var(--navbar-transition-easing),
            visibility var(--navbar-transition-duration) var(--navbar-transition-easing),
            transform var(--navbar-transition-duration) var(--navbar-transition-easing);

        &--open {
            opacity: 1;
            visibility: visible;
            transform: translateY(0);
        }
    }

    /* desktop */
    @include mq.screen_md(min) {
        .navbar__content {
            flex-direction: row;
            justify-content: space-between;
            flex: 1;
            height: 100%;

            /* Inline in the bar, so the bar surface shows through instead of this panel. */
            background-color: transparent;
            position: static;
            width: auto;
            top: auto;
            bottom: auto;
            opacity: 1;
            visibility: visible;
            overflow: visible;
            transform: none;
            transition: none;
            padding-top: 0;
        }
    }
</style>
